"use client";

import { ChevronLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  HIGHLIGHT_CONFIG,
  HighlightMaterials,
  HighlightCategory,
} from "./highlights";
import { useNotebookContext } from "../../../hooks/useNotebook";
import { useEffect, useMemo, useState } from "react";
import { daysAgo } from "./highlight-card";
import { getFileMimeType, getFileSizeText } from "../sources/source-viewer";
import { getSourceIcon } from "../course-source-list";

interface FocusedHighlightViewProps {
  highlight: HighlightMaterials;
  onBack: () => void;
  rightTopBar?: boolean;
}

export function FocusedHighlightView({
  highlight,
  onBack,
  rightTopBar,
}: FocusedHighlightViewProps) {
  const { getHighlightContents, sources, navigatePage } = useNotebookContext();
  const config = HIGHLIGHT_CONFIG.find((c) => c.id === highlight.type);
  const [contents, setContents] = useState();
  useEffect(() => {
    (async () => {
      setContents(await getHighlightContents(highlight));
    })();
  }, [highlight]);
  if (!config) return null;

  const highlightSources = useMemo(() => {
    const ids = (highlight.metadata?.document_ids as number[]) ?? [];
    return sources.filter((s) => ids.includes(s.id));
  }, [sources, highlight.metadata?.document_ids]);

  return (
    <div className="flex h-full w-full flex-col bg-theme-0">
      {/* Header */}
      <div
        className={cn(
          "flex items-center gap-1 h-16",
          rightTopBar && "justify-end mr-4"
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-1 text-sm hover:underline text-theme-0"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to chat
        </Button>

        <Separator
          orientation="vertical"
          className="mr-3 data-[orientation=vertical]:h-5"
        />

        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg mr-1",
            config.color.bg,
            config.color.border
          )}
        >
          <config.icon className={cn("h-5 w-5", config.color.text)} />
        </div>
        <div>
          <div className="text-sm font-semibold text-theme-0">
            {config.label}
          </div>
        </div>
      </div>

      <Separator />
      {/* Content */}
      <div className="flex-1 overflow-auto py-5">
        {highlight.contents || contents ? (
          <div className="mx-auto space-y-4">
            {/* Header */}
            <header className="flex flex-col gap-1.5 px-6">
              <h1 className="text-xl font-semibold text-theme-0 leading-tight">
                {highlight.name || "Untitled highlight"}
              </h1>

              <div className="flex flex-wrap items-center gap-2 text-xs text-theme-1">
                <span>
                  Created{" "}
                  {highlight.created_at
                    ? daysAgo(highlight.created_at)
                    : "Unknown date"}
                </span>

                <span className="opacity-40">•</span>

                <a
                  onClick={() =>
                    document
                      .getElementById("highlight-sources")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="text-theme-link hover:underline cursor-pointer"
                >
                  {highlightSources.length ? "View " : ""}
                  {highlight.metadata?.document_ids?.length ?? 0} source
                  {(highlight.metadata?.document_ids?.length ?? 0) === 1
                    ? ""
                    : "s"}
                </a>
              </div>
            </header>

            {/* Content frame */}
            <div className={cn("bg-theme-0", "py-6")}>
              <config.focusedComponent
                contents={highlight.contents || contents}
              />
            </div>
            {!!highlightSources.length && (
              <section
                id="highlight-sources"
                className="max-w-3xl mx-auto mt-14 mb-30 scroll-mt-20"
              >
                <Separator className="mb-6" />

                <header className="mb-4">
                  <h2 className="text-lg font-semibold text-theme-0">
                    Sources used
                  </h2>
                  <p className="text-xs text-theme-1 mt-1">
                    This material was generated with help from the sources
                    below. AI may be inaccurate or incomplete, so use these
                    sources to fact-check and confirm important details.
                  </p>
                </header>

                <div className="rounded-xl border border-theme divide-y overflow-auto">
                  {highlightSources.map((source) => {
                    const mime = getFileMimeType(source);
                    const Icon = getSourceIcon(mime);
                    const sizeText = getFileSizeText(source);
                    return (
                      <button
                        key={source.name}
                        onClick={() =>
                          navigatePage({
                            type: "source_highlight",
                            source: source,
                            highlight: highlight,
                          })
                        }
                        className={cn(
                          "group w-full text-left px-4 py-3",
                          "hover:bg-theme-1 transition"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {/* File icon */}
                          <div className="shrink-0 text-theme-1">
                            <Icon className="h-4 w-4" />
                          </div>

                          {/* Text */}
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-theme-link">
                              {source.name}
                            </div>

                            <div className="mt-0.5 text-xs text-theme-1">
                              {sizeText}
                              <span className="mx-1.5 opacity-40">•</span>
                              {source.source === "course"
                                ? "Course upload"
                                : "External"}
                              <span className="mx-1.5 opacity-40">•</span>
                              Added {daysAgo(source.created_at)}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-theme-1" />
          </div>
        )}
      </div>
    </div>
  );
}
